import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';

/**
 * Modal component using Preline overlay pattern
 *
 * A flexible modal/dialog component with header, body, and footer sections.
 * Uses Preline's overlay system for backdrop and animations.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler function
 * @param {string} [props.title] - Modal title
 * @param {React.ReactNode} props.children - Modal body content
 * @param {React.ReactNode} [props.footer] - Modal footer content (typically buttons)
 * @param {string} [props.size='md'] - Modal size ('sm', 'md', 'lg', 'xl')
 * @param {boolean} [props.closeButton=true] - Show close button in header
 * @param {string} [props.className] - Additional CSS classes for modal content
 * @param {string} [props.bodyClassName] - Additional CSS classes for modal body
 *
 * @example
 * <Modal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Delete File"
 *   footer={
 *     <>
 *       <Button variant="secondary" onClick={handleClose}>Cancel</Button>
 *       <Button variant="danger" onClick={handleDelete}>Delete</Button>
 *     </>
 *   }
 * >
 *   <p>Are you sure you want to delete this file?</p>
 * </Modal>
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeButton = true,
  className = '',
  bodyClassName = '',
}) {
  // Size classes
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-xl',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Ensure document.body exists before creating portal
  if (typeof document === 'undefined' || !document.body) {
    return null;
  }

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[80] bg-black/70 transition-opacity cursor-pointer"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none">
        <div
          className={`relative flex flex-col bg-surface border border-border-default shadow-lg rounded-2xl ${sizeClasses[size]} w-full max-h-[90vh] overflow-hidden pointer-events-auto ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {(title || closeButton) && (
            <div className="flex justify-between items-center py-4 px-6 border-b border-border-default">
              {title && (
                <h3 className="font-semibold text-default text-lg">
                  {title}
                </h3>
              )}
              {closeButton && (
                <button
                  type="button"
                  className="size-8 inline-flex justify-center items-center gap-x-2 rounded-full border border-transparent bg-hover-muted text-default hover:bg-border-strong focus:outline-none focus:bg-border-strong disabled:opacity-50 disabled:pointer-events-none ml-auto cursor-pointer"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <Icon icon="lucide:x" className="shrink-0 size-4" />
                </button>
              )}
            </div>
          )}

          {/* Body */}
          <div className={`p-6 overflow-y-auto ${bodyClassName}`}>
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="flex justify-end items-center gap-x-2 py-4 px-6 border-t border-border-default">
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  closeButton: PropTypes.bool,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
};
